import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { 
  PictureAsPdf, 
  Visibility, 
  CloudUpload, 
  Compare,
  FilePresent,
  CalendarToday 
} from "@mui/icons-material";
import SideBar from "../dashboard admin/components/SideBar";
import api from "../../../services/api/api";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const EditPublication = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [isPublished, setIsPublished] = useState(false);
  const [pdf, setPdf] = useState(null);
  const [publicationDate, setPublicationDate] = useState("");
  
  // Novos estados para PDF atual
  const [currentPdfUrl, setCurrentPdfUrl] = useState("");
  const [currentPdfName, setCurrentPdfName] = useState("");
  const [showComparison, setShowComparison] = useState(false);
  const [comparisonDialog, setComparisonDialog] = useState(false);

  // Obter token de autenticação
  const getAuthToken = () => {
    return localStorage.getItem("token");
  };

  // Extrair nome do arquivo da URL
  const extractFileName = (url) => {
    if (!url) return "";
    const parts = url.split('/');
    const fileName = parts[parts.length - 1];
    return decodeURIComponent(fileName);
  };

  // Formatar tamanho do arquivo
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Carregar os dados da publicação para edição
  useEffect(() => {
    const token = getAuthToken();

    if (token && id) {
      api
        .get(`api/getPublicationById/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((response) => {
          console.log("Dados da publicação:", response.data);
          const { name, category, isPublished, publicationDate, file } = response.data;

          setTitle(name);
          setCategory(category.id || category._id || category);
          setIsPublished(isPublished);
          setPublicationDate(publicationDate?.slice(0, 10));
          
          // Definir dados do PDF atual
          if (file) {
            setCurrentPdfUrl(file);
            setCurrentPdfName(extractFileName(file));
          }
        })
        .catch((error) => {
          console.error("Erro ao buscar publicação:", error);
          Swal.fire({
            icon: "error",
            title: "Erro!",
            text: "Erro ao carregar a publicação. Tente novamente.",
            confirmButtonColor: "#1cb5e0",
          });
        });
    }
  }, [id]);

  // Carregar as categorias para o select
  useEffect(() => {
    const token = getAuthToken();

    if (token) {
      api
        .get("api/categories", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((response) => {
          setCategories(response.data || []);
        })
        .catch((error) => {
          console.error("Erro ao buscar categorias:", error);
        });
    } else {
      console.error("Token de autenticação não encontrado.");
    }
  }, []);

  // Abrir PDF atual em nova aba
  const handleViewCurrentPdf = () => {
    if (currentPdfUrl) {
      window.open(currentPdfUrl, '_blank');
    }
  };

  // Visualizar novo PDF selecionado
  const handleViewNewPdf = () => {
    if (pdf) {
      const fileURL = URL.createObjectURL(pdf);
      window.open(fileURL, '_blank');
      // Limpar a URL após um tempo para liberar memória
      setTimeout(() => URL.revokeObjectURL(fileURL), 1000);
    }
  };

  // Lidar com seleção de novo PDF
  const handlePdfChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPdf(file);
      if (currentPdfUrl) {
        setShowComparison(true);
      }
    }
  };

  // Abrir dialog de comparação
  const handleShowComparison = () => {
    setComparisonDialog(true);
  };

  // Fechar dialog de comparação
  const handleCloseComparison = () => {
    setComparisonDialog(false);
  };

  // Remover novo PDF selecionado
  const handleRemoveNewPdf = () => {
    setPdf(null);
    setShowComparison(false);
    // Reset do input file
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Lidar com a submissão do formulário
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!title || !category || !publicationDate) {
      Swal.fire({
        icon: "warning",
        title: "Atenção!",
        text: "Preencha todos os campos obrigatórios!",
        confirmButtonColor: "#1cb5e0",
      });
      return;
    }

    const token = getAuthToken();

    if (!token) {
      console.error("Token de autenticação não encontrado.");
      return;
    }

    Swal.fire({
      title: "Atualizando...",
      text: "Aguarde enquanto a publicação é atualizada.",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    const formData = new FormData();
    formData.append("name", title);
    formData.append("category", category);
    formData.append("isPublished", isPublished);
    formData.append("publicationDate", publicationDate);
    if (pdf) {
      formData.append("file", pdf);
    }

    api
      .put(`api/updatePublication/${id}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      })
      .then(() => {
        Swal.fire({
          icon: "success",
          title: "Sucesso!",
          text: "Publicação atualizada com sucesso!",
          confirmButtonColor: "#1cb5e0",
        }).then(() => {
          navigate("/admin/publications");
        });
      })
      .catch((error) => {
        console.error("Erro ao atualizar publicação:", error);
        Swal.fire({
          icon: "error",
          title: "Erro!",
          text: "Erro ao atualizar a publicação. Tente novamente.",
          confirmButtonColor: "#1cb5e0",
        });
      });
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
      {/* Sidebar */}
      <Box>
        <SideBar />
      </Box>

      {/* Main Content */}
      <Box sx={{ flex: 1, p: 3 }}>
        <Typography variant="h4" fontWeight="bold" mb={3}>
          Editar Publicação
        </Typography>

        {/* Card do PDF Atual */}
        {currentPdfUrl && (
          <Card sx={{ mb: 3, maxWidth: "600px", mx: "auto" }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilePresent color="primary" />
                PDF Atual
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Nome do arquivo:
                  </Typography>
                  <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                    {currentPdfName}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<Visibility />}
                  onClick={handleViewCurrentPdf}
                  sx={{ textTransform: "none" }}
                >
                  Visualizar PDF
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Formulário */}
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            backgroundColor: "#fff",
            p: 3,
            borderRadius: 2,
            boxShadow: 3,
            maxWidth: "600px",
            mx: "auto",
          }}
        >
          <TextField
            label="Título"
            fullWidth
            variant="outlined"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            sx={{ mb: 2 }}
          />

          <TextField
            select
            label="Categoria"
            fullWidth
            variant="outlined"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            sx={{ mb: 2 }}
          >
            {categories.map((cat) => (
              <MenuItem key={cat._id} value={cat._id}>
                {cat.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Data de Publicação"
            type="date"
            fullWidth
            variant="outlined"
            value={publicationDate}
            onChange={(e) => setPublicationDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ mb: 2 }}
          />

          {/* Seção de Upload de PDF */}
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              component="label"
              fullWidth
              startIcon={<CloudUpload />}
              sx={{
                textTransform: "none",
                background: "linear-gradient(90deg, #1cb5e0, #000851)",
                mb: 1,
              }}
            >
              {pdf ? "Alterar PDF" : "Carregar Novo PDF"}
              <input
                type="file"
                hidden
                accept="application/pdf"
                onChange={handlePdfChange}
              />
            </Button>

            {/* Mostrar arquivo selecionado */}
            {pdf && (
              <Card variant="outlined" sx={{ mt: 2, bgcolor: '#f8f9fa' }}>
                <CardContent sx={{ py: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      <PictureAsPdf color="error" />
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          Novo PDF selecionado:
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {pdf.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatFileSize(pdf.size)}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Visibility />}
                        onClick={handleViewNewPdf}
                        sx={{ textTransform: "none" }}
                      >
                        Visualizar
                      </Button>
                      {showComparison && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Compare />}
                          onClick={handleShowComparison}
                          sx={{ textTransform: "none" }}
                        >
                          Comparar
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={handleRemoveNewPdf}
                        sx={{ textTransform: "none" }}
                      >
                        Remover
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={isPublished}
                onChange={(e) => setIsPublished(e.target.checked)}
                color="primary"
              />
            }
            label="Publicar"
            sx={{ mb: 2 }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{
              textTransform: "none",
              background: "linear-gradient(90deg, #1cb5e0, #000851)",
            }}
          >
            Atualizar Publicação
          </Button>
        </Box>

        {/* Dialog de Comparação */}
        <Dialog
          open={comparisonDialog}
          onClose={handleCloseComparison}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Compare />
              Comparação de Arquivos PDF
            </Box>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3}>
              {/* PDF Atual */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" color="primary" gutterBottom>
                      PDF Atual
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <PictureAsPdf color="primary" />
                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                        {currentPdfName}
                      </Typography>
                    </Box>
                    <Chip 
                      label="Arquivo atual" 
                      color="primary" 
                      size="small" 
                      sx={{ mb: 1 }}
                    />
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Visibility />}
                        onClick={handleViewCurrentPdf}
                        sx={{ textTransform: "none" }}
                      >
                        Visualizar
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Novo PDF */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" color="secondary" gutterBottom>
                      Novo PDF
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <PictureAsPdf color="secondary" />
                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                        {pdf?.name}
                      </Typography>
                    </Box>
                    <Chip 
                      label="Arquivo novo" 
                      color="secondary" 
                      size="small" 
                      sx={{ mb: 1 }}
                    />
                    <Typography variant="caption" display="block" color="text.secondary">
                      Tamanho: {pdf ? formatFileSize(pdf.size) : 'N/A'}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      Tipo: {pdf?.type || 'application/pdf'}
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Visibility />}
                        onClick={handleViewNewPdf}
                        sx={{ textTransform: "none" }}
                      >
                        Visualizar
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Atenção:</strong> Ao salvar as alterações, o arquivo atual será substituído pelo novo arquivo selecionado.
                Esta ação não pode ser desfeita.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseComparison}>Fechar</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default EditPublication;